<div class="space-y-6">
  @if (canvasEnabled) {
    <!-- TVG-103: view-switcher (Canvas / Both / Panel). Persists in localStorage. -->
    <div class="flex flex-wrap items-center gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">View</span>
      @for (option of viewOptions; track option.value) {
        <button
          type="button"
          (click)="setView(option.value)"
          [class]="view === option.value
            ? 'h-9 rounded-lg border border-brand-500 bg-brand-50 px-3 text-sm font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
            : 'h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:border-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'">
          @if (option.value === 'canvas') { <lucide-icon [name]="CanvasIcon" class="mr-1 inline-block h-3.5 w-3.5"></lucide-icon> }
          @if (option.value === 'panel') { <lucide-icon [name]="PanelIcon" class="mr-1 inline-block h-3.5 w-3.5"></lucide-icon> }
          {{ option.label }}
        </button>
      }
    </div>
  } @else {
    <div class="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-900/20 dark:text-blue-100">
      <p class="font-medium">Panel view</p>
      <p class="mt-1">
        Add tables, set seat counts, and assign guests from the list below. Every rule shown is
        advisory: only assigning a guest to a real seat at a real table is enforced.
      </p>
    </div>
  }

  @if (loading) {
    <div class="rounded-2xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-600 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400">
      Loading table plan…
    </div>
  }

  @if (loadError) {
    <div class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200">
      {{ loadError }}
    </div>
  }

  @if (!loading && !loadError && summary) {
    <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
          <lucide-icon [name]="ChairIcon" class="h-4 w-4"></lucide-icon>
          Seats placed
        </div>
        <div class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
          {{ summary.seat_count }}
        </div>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
          <lucide-icon [name]="UsersIcon" class="h-4 w-4"></lucide-icon>
          Guests on list
        </div>
        <div class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
          {{ summary.guest_count }}
        </div>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
          Assigned
        </div>
        <div class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
          {{ summary.assigned_count }} / {{ summary.seat_count }}
        </div>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
        <div class="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
          <lucide-icon [name]="BabyIcon" class="h-4 w-4"></lucide-icon>
          High chairs
        </div>
        <div class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
          {{ summary.high_chair_count }}
        </div>
      </div>
    </div>

    @if (summary.seat_count !== summary.guest_count
      || summary.over_venue_capacity
      || summary.tables_outside_room > 0
      || summary.tables_overlapping > 0
      || templateGuidanceExceeded !== null) {
      <div class="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-100">
        <div class="flex items-start gap-2">
          <lucide-icon [name]="AlertIcon" class="mt-0.5 h-4 w-4"></lucide-icon>
          <div class="space-y-1">
            @if (summary.seat_count !== summary.guest_count) {
              <p>
                {{ summary.seat_count }} seats placed for {{ summary.guest_count }} guests
                @if (summary.guests_without_seat > 0) {
                  — {{ summary.guests_without_seat }} guests without a seat yet
                }
                @if (summary.seat_count > summary.guest_count) {
                  — {{ summary.seat_count - summary.guest_count }} more seats than guests
                }
              </p>
            }
            @if (summary.over_venue_capacity) {
              <p>
                Seat count exceeds the venue's advisory capacity of {{ summary.venue_capacity }}.
              </p>
            }
            @if (summary.tables_outside_room > 0) {
              <p>
                {{ summary.tables_outside_room }} table(s) placed outside the room outline.
              </p>
            }
            @if (summary.tables_overlapping > 0) {
              <p>
                {{ summary.tables_overlapping }} table(s) are closer to another table than the walking space allows.
              </p>
            }
            @if (templateGuidanceExceeded !== null) {
              <p>
                {{ summary.seat_count }} seats is more than the {{ templateGuidanceExceeded }} the selected template is laid out for.
              </p>
            }
            <p class="text-amber-700 dark:text-amber-200/80">Advisory only — the plan still saves.</p>
          </div>
        </div>
      </div>
    }
  }

  @if (!loading && !loadError && showCanvas && plan && summary) {
    <!-- TVG-189: saved templates live in the canvas' left editor pane — preview
         on the canvas, then Select to lock one in for the event. -->
    <app-table-plan-canvas
      [eventId]="event.id"
      [plan]="plan"
      [summary]="summary"
      [guests]="guests"
      [venueName]="event.venue?.name ?? null"
      [venueTemplates]="venueTemplates"
      [guestCount]="totalGuestCount"
      [chosenLayoutKey]="chosenLayoutKey"
      [furniture]="furniture"
      [pinned]="!showPanel"
      (change)="onCanvasChange()"
      (error)="onCanvasError($event)"
      (saveTemplate)="saveAsTemplate($event)"
      (loadTemplate)="applyVenueTemplate($event)"
      (deleteTemplate)="deleteVenueTemplate($event)"
      (renameTemplate)="renameVenueTemplate($event)"
      (updateTemplateLayout)="updateVenueTemplateLayout($event)"
      (openGuest)="openGuest.emit($event)">
    </app-table-plan-canvas>
  }

  <!-- TVG-189: per-venue saved templates for the list-only panel view. With the
       canvas on, these move into the canvas' left editor pane (see the canvas
       "Templates" section) so they sit beside the room and never scroll behind
       it. -->
  @if (showVenueTemplatesCard) {
    <div class="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">Venue templates</h3>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Save the current arrangement as a reusable template for
            <span class="font-medium">{{ event.venue?.name ?? 'this venue' }}</span>, or load a saved one
            as a starting point. Loading replaces the current tables; everything stays editable.
          </p>
        </div>
        <app-button
          label="Save current layout as template"
          style="secondary"
          [disabled]="!hasTables || saving"
          (click)="saveAsTemplate()">
        </app-button>
      </div>

      @if (venueTemplates.length > 0) {
        <div class="mt-4 flex flex-wrap items-center gap-2">
          <select
            [(ngModel)]="selectedVenueTemplateId"
            class="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white">
            <option [ngValue]="null">Choose a saved template…</option>
            @for (template of venueTemplates; track template.id) {
              <option [ngValue]="template.id">
                {{ template.name }} ({{ template.seat_total }} seats · {{ template.table_count }} tables)
              </option>
            }
          </select>
          <app-button
            label="Load template"
            style="primary"
            [disabled]="selectedVenueTemplateId === null || saving"
            (click)="applyVenueTemplate()">
          </app-button>
        </div>

        <ul class="mt-4 space-y-2">
          @for (template of venueTemplates; track template.id) {
            <li class="flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm dark:border-gray-800 dark:bg-gray-900/40">
              <span class="text-gray-800 dark:text-gray-200">
                <span class="font-medium">{{ template.name }}</span>
                <span class="text-gray-500 dark:text-gray-400"> — {{ template.seat_total }} seats · {{ template.table_count }} tables</span>
              </span>
              <span class="flex shrink-0 items-center gap-3">
                <!-- TVG-227: rename or save the current layout back onto this template. -->
                <button
                  type="button"
                  class="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50 dark:text-brand-300"
                  [disabled]="saving"
                  (click)="promptRenameVenueTemplate(template)">
                  Rename
                </button>
                <button
                  type="button"
                  class="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50 dark:text-brand-300"
                  [disabled]="saving || !hasTables"
                  (click)="updateVenueTemplateLayout(template)">
                  Save layout
                </button>
                <button
                  type="button"
                  class="text-xs font-medium text-red-600 hover:underline disabled:opacity-50 dark:text-red-400"
                  [disabled]="saving"
                  (click)="deleteVenueTemplate(template)">
                  Delete
                </button>
              </span>
            </li>
          }
        </ul>
      } @else {
        <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">
          No saved templates for this venue yet. Arrange a layout, then save it here.
        </p>
      }
    </div>
  }

  @if (!loading && !loadError && showPanel && presets.length > 0) {
    <div class="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Admin preset</h3>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Apply a saved arrangement from the catalogue instead. Replaces the current tables.
      </p>
      <div class="mt-3 flex flex-wrap items-center gap-2">
        <select
          [(ngModel)]="selectedPresetId"
          class="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white">
          <option [ngValue]="null">Choose a preset…</option>
          @for (preset of presets; track preset.id) {
            <option [ngValue]="preset.id">{{ preset.name }} ({{ preset.total_seats }} seats)</option>
          }
        </select>
        <app-button
          label="Apply preset"
          style="primary"
          [disabled]="selectedPresetId === null || saving"
          (click)="applyPreset()">
        </app-button>
      </div>
    </div>
  }

  @if (!loading && !loadError && showPanel) {
    <div class="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">Add a table</h3>
      <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_140px_auto]">
        <input
          type="text"
          placeholder="Table label (e.g. Top table)"
          [(ngModel)]="newTableLabel"
          class="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
        <input
          type="number"
          min="0"
          max="64"
          [(ngModel)]="newTableSeatCount"
          class="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
        <app-button
          label="Add table"
          [icon]="PlusIcon"
          [disabled]="saving"
          (click)="addTable()">
        </app-button>
      </div>
    </div>

    <div class="space-y-3">
      @for (table of plan?.tables ?? []; track table.id) {
        <div class="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="flex flex-wrap items-center gap-3 p-4">
            <div class="flex-1 min-w-[200px]">
              <label class="block text-xs font-medium uppercase tracking-wide text-gray-500">Label</label>
              <input
                type="text"
                [value]="table.label"
                (blur)="renameTable(table, $any($event.target).value)"
                class="mt-1 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
            </div>
            <div class="w-32">
              <label class="block text-xs font-medium uppercase tracking-wide text-gray-500">Seats</label>
              <input
                type="number"
                min="0"
                max="64"
                [value]="table.seat_count"
                (change)="updateSeatCount(table, $any($event.target).value)"
                class="mt-1 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
            </div>
            <div class="flex items-end gap-2">
              <app-button
                [label]="isExpanded(table.id) ? 'Hide seats' : 'Show seats'"
                style="secondary"
                (click)="toggleExpanded(table.id)">
              </app-button>
              <app-button
                label="Delete"
                [icon]="TrashIcon"
                style="danger"
                [disabled]="saving"
                (click)="deleteTable(table)">
              </app-button>
            </div>
          </div>

          @if (isExpanded(table.id)) {
            <div class="border-t border-gray-100 p-4 dark:border-gray-800">
              <p class="mb-3 text-xs text-gray-500">Seats numbered clockwise from the top right of the table.</p>
              @if (table.seats.length === 0) {
                <p class="text-sm text-gray-600 dark:text-gray-400">No seats. Set the seat count above to add some.</p>
              } @else {
                <div class="space-y-2">
                  @for (seat of table.seats; track seat.id) {
                    <div class="grid grid-cols-1 gap-2 rounded-lg border border-gray-100 bg-gray-50 p-3 sm:grid-cols-[60px_1fr] sm:items-center dark:border-gray-800 dark:bg-gray-900/40">
                      <div class="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Seat {{ seat.seat_number }}
                      </div>
                      <select
                        [ngModel]="seat.event_guest_id"
                        (ngModelChange)="assignGuest(seat.id, $event)"
                        class="h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                        <option [ngValue]="null">— Unassigned —</option>
                        @for (guest of availableGuestsForSeat(seat.event_guest_id); track guest.id) {
                          <option [ngValue]="guest.id">
                            {{ guest.full_name }}@if (guest.guest_type === 'Child' || guest.guest_type === 'Baby') { · {{ guest.guest_type }} }
                          </option>
                        }
                      </select>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      } @empty {
        <div class="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-white/[0.03] dark:text-gray-400">
          No tables yet. Use a quick-setup preset or add a table to get started.
        </div>
      }
    </div>
  }
</div>
